/** Field sets for the five sections. Validation and totals come from apps/mobile/src/core/forms.ts. */
import { useMemo, useState } from "react";
import { INFLUENCE } from "@npac/rules";
import {
  AGE_KEYS,
  MAX_WORKERS,
  demographicsTotals,
  newCasteRow,
  newWorkerRow,
  type CasteRow,
  type FormBySection,
  type Influence,
  type AgentKey,
  type PersonForm,
  type WorkerRow,
} from "@core/forms";
import type { FieldSection } from "@core/types";
import type { Strings } from "../i18n";
import { useApp } from "../ui/context";
import { Banner, Button, Card, Choice, Field, NumberField, PhoneField, Toggle } from "../ui/components";

interface Props<S extends FieldSection> {
  form: FormBySection[S];
  setForm: (f: FormBySection[S]) => void;
  errors: Record<string, string>;
  editable: boolean;
  /**
   * What the server already has (counts only, never values: N-PAC, 28 Oct 2026). The phone only adds,
   * so a part that is already filled shows "✓ already filled" instead of an input.
   */
  have: Record<string, number>;
}

/** A part the server already has: shown as filled, not editable on the phone. */
export function Filled({ label }: { label: string }) {
  const { t } = useApp();
  return (
    <div className="ao-filled">
      <span>{label}</span>
      <b>{t.aoFilled}</b>
    </div>
  );
}

export function IdentityFields({ form, setForm, errors, have }: Props<"identity">) {
  const { t } = useApp();
  const set = (k: keyof typeof form) => (v: string) => setForm({ ...form, [k]: v });
  const row = (k: keyof typeof form, label: string, max: number, multiline = false) =>
    have[k] ? <Filled key={k} label={label} /> : <Field key={k} label={label} name={k} value={form[k]} onChange={set(k)} error={errors[k]} maxLength={max} multiline={multiline} />;
  return (
    <>
      <p className="muted">{t.identityHelp}</p>
      {row("mandal", t.mandal, 120)}
      {row("panchayat", t.panchayat, 120)}
      {row("village", t.village, 120)}
      {row("landmark", t.landmark, 200, true)}
    </>
  );
}

export function AgentsFields({ form, setForm, errors, editable, have }: Props<"agents">) {
  const { t } = useApp();
  const person = (key: AgentKey, title: string) => {
    // a draft saved on the phone before BLA-2 / BLO were added has no such keys
    const p = form[key] ?? { name: "", phone: "" };
    const set = (patch: Partial<PersonForm>) => setForm({ ...form, [key]: { ...p, ...patch } });
    if (have[key]) return <Filled label={title} />;
    return (
      <Card>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <Field label={t.agentName} name={`${key}.name`} value={p.name} onChange={(v) => set({ name: v })} error={errors[`${key}.name`]} maxLength={120} />
        <PhoneField label={t.agentPhone} name={`${key}.phone`} value={p.phone} onChange={(v) => set({ phone: v })} error={errors[`${key}.phone`] ?? errors[key]} />
        {editable && (p.name || p.phone) ? (
          <Button variant="ghost" onClick={() => set({ name: "", phone: "" })}>
            {t.clearPerson}
          </Button>
        ) : null}
      </Card>
    );
  };
  return (
    <>
      {person("pollingAgent", t.pollingAgent)}
      {person("coPollingAgent", t.coPollingAgent)}
      {person("bla2", t.bla2)}
      {person("blo", t.blo)}
    </>
  );
}

export function KaryakartaFields({ form, setForm, errors, editable, have }: Props<"karyakarta">) {
  const { t } = useApp();
  const rows = form.rows;
  const onServer = have.people ?? 0;
  // the server gives the new names the free slots of 20; the phone only knows how many are taken
  const room = Math.max(0, MAX_WORKERS - onServer);
  const update = (key: string, patch: Partial<WorkerRow>) => setForm({ rows: rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  const add = () => {
    const r = newWorkerRow(rows);
    if (r) setForm({ rows: [...rows, r] });
  };
  const remove = (row: WorkerRow, i: number) => {
    if ((row.name.trim() || row.mobile.trim()) && !window.confirm(t.removeWorkerConfirm(i + 1))) return;
    setForm({ rows: rows.filter((r) => r.key !== row.key) });
  };
  const influence = INFLUENCE.map((v) => ({ value: v as Influence, label: t[`inf_${v}` as const] }));

  return (
    <>
      <div className="ao-count">
        <b>{onServer}/20</b>
        <span>{t.aoMembersLabel}</span>
      </div>
      {errors.people ? <p className="error">⚠ {errors.people}</p> : null}
      {room === 0 ? <Banner kind="info">{t.aoMembersFull}</Banner> : null}
      {rows.map((r, i) => {
        const e = (f: string) => errors[`people.${r.key}.${f}`];
        return (
          <Card key={r.key}>
            <div className="card-head">
              <h3 style={{ color: "var(--primary)" }}>{t.aoNewMember(i + 1)}</h3>
              {editable ? (
                <Button variant="danger" onClick={() => remove(r, i)} className="w-auto" ariaLabel={`${t.remove} ${i + 1}`}>
                  ✕ {t.remove}
                </Button>
              ) : null}
            </div>
            <Field label={t.name} value={r.name} onChange={(v) => update(r.key, { name: v })} error={e("name")} maxLength={120} />
            <PhoneField label={t.mobile} value={r.mobile} onChange={(v) => update(r.key, { mobile: v })} error={e("mobile")} />
            <Toggle label={`${t.whatsapp}: ${t.sameAsMobile}`} checked={r.sameAsMobile} onChange={(v) => update(r.key, { sameAsMobile: v })} disabled={!editable} />
            {!r.sameAsMobile ? (
              <PhoneField label={`${t.whatsapp} (${t.optional})`} value={r.whatsapp} onChange={(v) => update(r.key, { whatsapp: v })} error={e("whatsapp")} />
            ) : e("whatsapp") ? (
              <p className="error">⚠ {e("whatsapp")}</p>
            ) : null}
            <Field label={t.role} value={r.role} onChange={(v) => update(r.key, { role: v })} error={e("role")} maxLength={80} />
            <Field label={t.caste} value={r.caste} onChange={(v) => update(r.key, { caste: v })} error={e("caste")} maxLength={80} />
            <div className="label">{t.influence}</div>
            <Choice name={t.influence} options={influence} value={r.influence} onChange={(v) => update(r.key, { influence: v })} disabled={!editable} />
          </Card>
        );
      })}
      {editable && room > 0 ? (
        rows.length < room ? (
          <Button variant="secondary" big onClick={add}>
            {t.addWorker}
          </Button>
        ) : (
          <p className="muted">{t.maxWorkers}</p>
        )
      ) : null}
    </>
  );
}

const ageLabel = (t: Strings, k: (typeof AGE_KEYS)[number]) => ({ a18_25: t.age18_25, a26_40: t.age26_40, a41_60: t.age41_60, a60plus: t.age60plus })[k];

/** Live totals; a mismatch is a clear warning but never blocks saving (DECISIONS.md #4). */
export function DemographicsFields({ form, setForm, errors, editable }: Props<"demographics">) {
  const { t } = useApp();
  const totals = useMemo(() => demographicsTotals(form), [form]);
  const setCaste = (key: string, patch: Partial<CasteRow>) => setForm({ ...form, castes: form.castes.map((c) => (c.key === key ? { ...c, ...patch } : c)) });

  return (
    <>
      <NumberField label={t.totalVoters} name="totalVoters" value={form.totalVoters} onChange={(v) => setForm({ ...form, totalVoters: v })} error={errors.totalVoters} />

      <h3>{t.ageGroups}</h3>
      <div className="grid2">
        {AGE_KEYS.map((k) => (
          <NumberField key={k} label={ageLabel(t, k)} name={`age.${k}`} value={form.age[k]} onChange={(v) => setForm({ ...form, age: { ...form.age, [k]: v } })} error={errors[`age.${k}`]} />
        ))}
      </div>
      <TotalLine label={t.ageSum} sum={totals.ageSum} total={totals.total} matches={totals.ageMatches} t={t} testId="age-total" />

      <h3>{t.casteTable}</h3>
      {errors.castes ? <p className="error">⚠ {errors.castes}</p> : null}
      {form.castes.map((c, i) => {
        const e = (f: string) => errors[`castes.${c.key}.${f}`];
        return (
          <Card key={c.key}>
            <div className="row" style={{ alignItems: "flex-end" }}>
              <Field label={`${i + 1}. ${t.casteName}`} value={c.name} onChange={(v) => setCaste(c.key, { name: v })} error={e("name")} maxLength={80} />
              {editable ? (
                <div style={{ flex: "none", width: 64, marginBottom: 16 }}>
                  <Button variant="danger" onClick={() => setForm({ ...form, castes: form.castes.filter((x) => x.key !== c.key) })} ariaLabel={`${t.remove} ${c.name}`}>
                    ✕
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="grid2">
              <NumberField label={t.male} value={c.male} onChange={(v) => setCaste(c.key, { male: v })} error={e("male")} />
              <NumberField label={t.female} value={c.female} onChange={(v) => setCaste(c.key, { female: v })} error={e("female")} />
            </div>
            <Toggle label={`⭐ ${t.core}`} checked={c.isCore} onChange={(v) => setCaste(c.key, { isCore: v })} disabled={!editable} />
          </Card>
        );
      })}
      {editable ? (
        <Button variant="secondary" onClick={() => setForm({ ...form, castes: [...form.castes, newCasteRow()] })}>
          {t.addCaste}
        </Button>
      ) : null}
      <div style={{ height: 16 }} />
      <NumberField label={t.otherGender} name="otherGender" value={form.otherGender} onChange={(v) => setForm({ ...form, otherGender: v })} error={errors.otherGender} />

      <div className="totals" aria-live="polite">
        <b>{t.genderTotals}</b>
        <div className="stats" style={{ marginTop: 8 }}>
          <div className="stat">
            <b>{totals.gender?.male ?? "—"}</b>
            <span>{t.male}</span>
          </div>
          <div className="stat">
            <b>{totals.gender?.female ?? "—"}</b>
            <span>{t.female}</span>
          </div>
          <div className="stat">
            <b>{totals.gender?.other ?? "—"}</b>
            <span>{t.otherGender}</span>
          </div>
        </div>
        <TotalLine label={t.casteSum} sum={totals.casteSum} total={totals.total} matches={totals.casteMatches} t={t} testId="caste-total" />
      </div>
    </>
  );
}

function TotalLine({ label, sum, total, matches, t, testId }: { label: string; sum: number | null; total: number | null; matches: boolean | null; t: Strings; testId: string }) {
  if (sum == null) return null;
  const bad = matches === false;
  return (
    <div className={`total-line${bad ? " bad" : matches ? " ok" : ""}`} role={bad ? "alert" : undefined} data-testid={testId}>
      {label}: {sum}
      {total != null ? ` / ${t.totalLabel} ${total}` : ""} {matches ? `✓ ${t.matches}` : bad ? "✗" : ""}
      {bad && total != null ? <div className="warn-text">{t.mismatchWarn(sum, total)}</div> : null}
      {total == null ? <div className="warn-text muted">{t.enterTotalFirst}</div> : null}
    </div>
  );
}

export function GroundFields({ form, setForm, errors, have }: Props<"ground">) {
  const { t } = useApp();
  const setAt = (key: "problems" | "suggestions", i: number, v: string) => {
    const next = [...form[key]];
    next[i] = v;
    setForm({ ...form, [key]: next });
  };
  const listError = (key: string) => Object.entries(errors).find(([k]) => k === key || k.startsWith(`${key}.`))?.[1];
  // as many as needed: show the filled lines plus one empty (at least 3), and an "add" button
  const shown = (a: string[]) => Math.min(a.length, Math.max(3, a.reduce((last, v, i) => (v.trim() ? i + 1 : last), 0) + 1));
  const [more, setMore] = useState({ problems: 0, suggestions: 0 });
  const list = (key: "problems" | "suggestions", label: (n: number) => string, addLabel: string) => {
    // the server already has have[key] lines; new ones are added after them
    const onServer = have[key] ?? 0;
    const room = Math.max(0, form[key].length - onServer);
    const n = Math.min(room, shown(form[key]) + more[key]);
    return (
      <>
        <div className="ao-line">{t.aoLines(key === "problems" ? t.aoProblems : t.aoSuggestions, onServer)}</div>
        {room === 0 ? <p className="muted">{t.aoLinesFull}</p> : null}
        {form[key].slice(0, n).map((v, i) => (
          <Field key={`${key}${i}`} label={label(onServer + i + 1)} name={`${key}.${i}`} value={v} onChange={(x) => setAt(key, i, x)} maxLength={300} multiline />
        ))}
        {n < room ? (
          <Button variant="secondary" onClick={() => setMore((m) => ({ ...m, [key]: m[key] + 1 }))}>
            ＋ {addLabel}
          </Button>
        ) : null}
        {listError(key) ? <p className="error">⚠ {listError(key)}</p> : null}
      </>
    );
  };
  return (
    <>
      <h3>{t.problems}</h3>
      {list("problems", t.problemN, t.addProblem)}
      <h3>{t.suggestions}</h3>
      {list("suggestions", t.suggestionN, t.addSuggestion)}
    </>
  );
}
